import { Button, Link, Typography } from '@globalmed/atlas-ui';
import { useTranslation } from 'react-i18next';
import styles from './InfoText.module.css';
import { useMediaQuery } from '@/hooks/useMediaQuery';
import { useInstitutionsStore } from '@/stores/institutions';
import { useListStore } from '@/stores/list';
import { formatPhoneNumberWithCode } from '@/utils/formatPhoneNumber';
import { useKioskStore } from '@/stores/kiosk';
import { checkIsKiosk } from '@/utils/kiosk';
import { TInstitutionServerCamel, TNullable } from '@/types';

type TProps = {
  onClose: () => void;
};

const getInfoTextByInstitution = (
  institution: TNullable<TInstitutionServerCamel>,
) => {
  const phone = institution?.pocSettings?.phone;
  const email = institution?.pocSettings?.email;
  return { phone, email };
};

/**
 * Renders contact information for the current institution's administrator.
 * Displays phone number and email if available, and a confirmation button to close.
 * Adjusts spacing for small screens.
 * @param props - Component props.
 * @param props.onClose - Callback invoked when the confirmation button is clicked.
 * @returns InfoText component.
 */
export const InfoText = ({ onClose }: TProps) => {
  const { t } = useTranslation();
  const currentInstitution = useInstitutionsStore(
    (state) => state.currentInstitution,
  );
  const kioskInstitution = useKioskStore((state) => state.kioskInstitution);
  const countries = useListStore((state) => state.countries);
  const isSmallScreen = useMediaQuery('(max-width: 768px)');
  const isKiosk = checkIsKiosk();

  const { phone, email } = getInfoTextByInstitution(
    isKiosk ? kioskInstitution : currentInstitution,
  );

  const formattedPhone = phone?.number
    ? formatPhoneNumberWithCode(phone, countries ?? [])
    : null;

  return (
    <>
      <Typography
        mb={isSmallScreen ? '16px' : '24px'}
        sx={{ color: 'var(--colors-secondary)' }}
      >
        {t('please-contact-your-institution-administrator-for-help')}
      </Typography>

      <div className={styles.infoTextRow}>
        <Typography>{t('common.phone-number')}</Typography>

        {formattedPhone ? (
          <Link
            href={`tel:${formattedPhone.replace(/[^\d+]/g, '')}`}
            target="_self"
            text={formattedPhone}
            size="sm"
            color="black"
            sx={{ fontWeight: 500 }}
          />
        ) : (
          <Typography fontWeight={500}>{t('account-settings.n-a')}</Typography>
        )}
      </div>

      <div className={styles.infoTextRow}>
        <Typography>{t('common.email')}</Typography>

        {email ? (
          <Link
            href={`mailto:${email}`}
            target="_self"
            text={email}
            size="sm"
            color="black"
            sx={{ fontWeight: 500 }}
          />
        ) : (
          <Typography fontWeight={500}>{t('account-settings.n-a')}</Typography>
        )}
      </div>

      <div className={styles.confirmButtonLine}>
        <Button text={t('got-it')} onClick={onClose} wide={isSmallScreen} />
      </div>
    </>
  );
};
